<script lang="ts">
  import { api } from '$shared/api';
  import { getUser, isLoggedIn } from '$lib/auth.svelte';
  import { trackEvent, trackProfileView } from '$lib/analytics.svelte';
  import { addToast } from '$shared/toasts.svelte';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import ProfileAchievements from '$components/profile/ProfileAchievements.svelte';
  import ProfileTrophies from '$components/profile/ProfileTrophies.svelte';
  import ProfileAcoustic from '$components/profile/ProfileAcoustic.svelte';
  import ProfileListeningIdentity from '$components/profile/ProfileListeningIdentity.svelte';
  import ProfileMusicDna from '$components/profile/ProfileMusicDna.svelte';
  import ProfileSeasonTrophies from '$components/profile/ProfileSeasonTrophies.svelte';
  import ProfileRecentDuels from '$components/profile/ProfileRecentDuels.svelte';
  import ProfileRecentTracks from '$components/profile/ProfileRecentTracks.svelte';
  import ProfileGenres from '$components/profile/ProfileGenres.svelte';
  import ProfileSavedTracks from '$components/profile/ProfileSavedTracks.svelte';
  import type { UserProfileResponse } from '$types/index';

  interface GenreGroup {
    genre_name: string;
    styles: { key: string; label: string }[];
  }
  interface SavedItem {
    source: 'duel' | 'pool';
    id: number;
    title: string | null;
    artist: string | null;
    style: string | null;
    submitter: string | null;
    avatar_url: string | null;
    duel_id: number | null;
    duel_name: string | null;
    type: string;
    youtube_id: string | null;
    soundcloud_url: string | null;
    tiktok_url: string | null;
    yt_view_count: number | null;
    lb_listen_count: number | null;
    favorited_at: string;
  }

  interface Achievement {
    icon: string;
    name: string;
    desc: string;
    earned: boolean;
  }
  interface RecentDuel {
    id: number;
    duel_name: string;
    end_vote: string;
    track_type: string;
    genre_name: string;
    rank: number;
    my_votes: number;
    participant_count: number;
    total_votes: number;
  }
  interface UserTrack {
    id: number;
    duel_id: number;
    track_type: string;
    name: string;
    duel_name: string;
    genre_name: string;
    votes: number;
    listens: number;
    rank: number;
    total_tracks: number;
    end_vote: string;
    created_at: string;
  }

  let { username }: { username: string } = $props();

  let profile = $state<UserProfileResponse | null>(null);
  let points = $state(0);
  let seasonPoints = $state(0);
  let rank = $state(0);
  let followers = $state(0);
  let following = $state(0);
  let isFollowingUser = $state(false);
  let isBlockedByMe = $state(false);
  let blocksMe = $state(false);
  let menuOpen = $state(false);
  let menuWrapEl = $state<HTMLElement>();
  let genreFingerprint = $state<Record<string, number> | null>(null);
  let voteStreak = $state(0);
  let loading = $state(true);
  let followLoading = $state(false);
  let blockLoading = $state(false);
  let error = $state(false);

  interface ListeningIdentity {
    contrarian_pct: number | null;
    early_adopter_pct: number | null;
    genre_breadth: number | null;
    taste_consistency_pct: number | null;
    listening_depth: number | null;
    sufficient_data: boolean;
  }

  interface AcousticProfile {
    has_profile: boolean;
    voted: {
      avg_bpm: number | null;
      avg_danceability: number | null;
      avg_energy: number | null;
      vote_count: number;
    } | null;
    submitted: {
      avg_bpm: number | null;
      avg_danceability: number | null;
      avg_energy: number | null;
      track_count: number;
    } | null;
    top_key: string | null;
  }

  // Secondary data
  let achievements = $state<Achievement[]>([]);
  let identity = $state<ListeningIdentity | null>(null);
  let acousticProfile = $state<AcousticProfile | null>(null);
  let recentDuels = $state<RecentDuel[]>([]);
  let recentTracks = $state<UserTrack[]>([]);
  let favGenres = $state<GenreGroup[]>([]);
  let savedItems = $state<SavedItem[]>([]);

  const currentUser = $derived(getUser());
  const isOwnProfile = $derived(currentUser?.username === username);

  $effect(() => {
    loadProfile();
  });

  // Close the 3-dot menu on outside-click and Escape. Listeners are only
  // active while the menu is open so the rest of the page isn't taxed.
  $effect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuWrapEl && !menuWrapEl.contains(e.target as Node)) menuOpen = false;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') menuOpen = false;
    }
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  });

  async function loadProfile() {
    loading = true;
    error = false;
    try {
      const data = await api.get<UserProfileResponse>(`/users/by-username/${encodeURIComponent(username)}`);
      profile = data;
      points = data.points ?? 0;
      seasonPoints = data.season_points ?? 0;
      rank = data.rank ?? 0;
      followers = data.followers ?? 0;
      following = data.following ?? 0;
      isFollowingUser = data.is_following ?? false;
      isBlockedByMe = data.is_blocked_by_me ?? false;
      blocksMe = data.blocks_me ?? false;
      genreFingerprint = data.genre_fingerprint ?? null;
      voteStreak = data.vote_streak ?? 0;

      // Track profile view
      if (data.id) {
        trackProfileView(data.id);
      }

      // Fetch secondary data in parallel (non-blocking)
      if (data.id) {
        loadSecondaryData(data.id);
      }
    } catch {
      error = true;
    }
    loading = false;
  }

  async function loadSecondaryData(userId: number) {
    const [achRes, duelsRes, tracksRes, liRes, apRes] = await Promise.all([
      api.get<{ achievements: Achievement[] }>(`/users/${userId}/achievements`).catch((e) => {
        console.warn('Failed to load achievements:', e);
        return { achievements: [] };
      }),
      api.get<{ duels: RecentDuel[] }>(`/users/${userId}/recent-duels`).catch((e) => {
        console.warn('Failed to load recent duels:', e);
        return { duels: [] };
      }),
      api.get<{ tracks: UserTrack[] }>(`/users/${userId}/public-tracks`).catch((e) => {
        console.warn('Failed to load public tracks:', e);
        return { tracks: [] };
      }),
      api.get<ListeningIdentity>(`/users/${userId}/listening-identity`).catch((e) => {
        console.warn('Failed to load listening identity:', e);
        return null;
      }),
      api.get<AcousticProfile>(`/users/${userId}/acoustic-profile`).catch((e) => {
        console.warn('Failed to load acoustic profile:', e);
        return null;
      }),
    ]);
    achievements = achRes.achievements;
    identity = liRes;
    recentDuels = duelsRes.duels;
    recentTracks = tracksRes.tracks;
    acousticProfile = apRes;

    // Additional profile data (non-blocking)
    api
      .get<{ genres: GenreGroup[] }>(`/users/${userId}/genres`)
      .then((d) => (favGenres = d.genres ?? []))
      .catch((e) => console.warn('Failed to load favorite genres:', e));
    api
      .get<{ items: SavedItem[] }>(`/users/${userId}/saved`)
      .then((d) => (savedItems = d.items ?? []))
      .catch((e) => console.warn('Failed to load saved tracks:', e));
  }

  async function toggleFollow() {
    if (!profile || !isLoggedIn() || isOwnProfile) return;
    followLoading = true;
    try {
      if (isFollowingUser) {
        await api.delete(`/follow/${profile.id}`);
        isFollowingUser = false;
        followers--;
      } else {
        await api.post('/follow', { followee_id: profile.id });
        isFollowingUser = true;
        followers++;
      }
      trackEvent('follow', {
        target_id: profile.id,
        target_type: 'user',
        metadata: { action: isFollowingUser ? 'unfollow' : 'follow' },
      });
    } catch (e) {
      console.warn('Follow toggle failed:', e);
    }
    followLoading = false;
  }

  async function toggleBlock() {
    if (!profile || !isLoggedIn() || isOwnProfile) return;
    menuOpen = false;
    blockLoading = true;
    try {
      if (isBlockedByMe) {
        await api.delete(`/users/${profile.id}/block`);
        isBlockedByMe = false;
        addToast(`Unblocked @${profile.username}`, 'join');
      } else {
        await api.post(`/users/${profile.id}/block`, {});
        isBlockedByMe = true;
        // Blocking clears follow relationships server-side; mirror locally.
        if (isFollowingUser) {
          isFollowingUser = false;
          followers = Math.max(0, followers - 1);
        }
        addToast(`Blocked @${profile.username}`, 'join');
      }
    } catch (e) {
      console.warn('Block toggle failed:', e);
      addToast('Could not update block', 'error');
    }
    blockLoading = false;
  }
</script>

<div>
  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else if profile}
    <div class="profile-header">
      <Avatar username={profile.username} avatarUrl={profile.avatar_url} size="lg" />
      <div class="profile-info">
        <h1>{profile.username}</h1>
        <div class="stats-row">
          <span><strong>{seasonPoints.toLocaleString()}</strong> <small>pts this week</small></span>
          <span><strong>{points.toLocaleString()}</strong> <small>pts all-time</small></span>
          <span><strong>#{rank}</strong> <small>rank</small></span>
          <span><strong>{followers}</strong> <small>followers</small></span>
          <span><strong>{following}</strong> <small>following</small></span>
        </div>
        {#if voteStreak >= 2}
          <div class="streak-badge">{'\u{1F525}'} {voteStreak}-day voting streak</div>
        {/if}
        {#if isLoggedIn() && !isOwnProfile}
          <div class="profile-actions">
            {#if blocksMe}
              <div class="blocks-me-banner">This user has blocked you.</div>
            {:else if !isBlockedByMe}
              <button
                class="btn {isFollowingUser ? 'btn-outline btn-unfollow' : 'btn-primary'} btn-pill follow-btn"
                onclick={toggleFollow}
                disabled={followLoading}>{isFollowingUser ? 'Unfollow' : 'Follow'}</button
              >
            {/if}
            <div class="menu-wrap" bind:this={menuWrapEl}>
              <button
                class="menu-trigger"
                aria-label="More actions"
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onclick={() => (menuOpen = !menuOpen)}>⋯</button
              >
              {#if menuOpen}
                <div class="menu" role="menu">
                  <button
                    class="menu-item menu-item--danger"
                    onclick={toggleBlock}
                    disabled={blockLoading}
                    role="menuitem"
                  >
                    {isBlockedByMe ? 'Unblock' : 'Block'} @{profile.username}
                  </button>
                </div>
              {/if}
            </div>
          </div>
          {#if isBlockedByMe}
            <div class="block-status">You've blocked this user.</div>
          {/if}
        {/if}
      </div>
    </div>

    <ProfileListeningIdentity {identity} />
    <ProfileMusicDna {genreFingerprint} />
    <ProfileGenres genres={favGenres} />

    {#if acousticProfile?.has_profile}
      <ProfileAcoustic {acousticProfile} />
    {/if}

    <ProfileSeasonTrophies seasonWins={profile?.season_wins} />

    <ProfileTrophies userId={profile?.id ?? null} />

    <ProfileAchievements {achievements} />

    <ProfileRecentDuels duels={recentDuels} />
    <ProfileRecentTracks tracks={recentTracks} />
    <ProfileSavedTracks items={savedItems} isOwn={isOwnProfile} />
  {:else if error}
    <p class="empty">
      Something went wrong loading this profile. <button class="retry-link" onclick={loadProfile}>Try again</button>
    </p>
  {:else}
    <p class="empty">User not found.</p>
  {/if}
</div>

<style>
  .profile-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }
  .profile-info {
    flex: 1;
  }
  .profile-info h1 {
    margin: 0;
  }
  .stats-row {
    display: flex;
    gap: 1rem;
    margin-top: 0.75rem;
    font-size: 0.875rem;
  }
  .stats-row small {
    color: var(--text-muted);
  }
  .follow-btn {
    margin-top: 0;
  }
  .btn-unfollow:hover {
    border-color: var(--error);
    color: var(--error);
  }
  .profile-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }
  .menu-wrap {
    position: relative;
  }
  .menu-trigger {
    background: none;
    border: 1px solid var(--border);
    color: var(--text-muted);
    border-radius: 999px;
    width: 2rem;
    height: 2rem;
    cursor: pointer;
    font-size: 1.1rem;
    line-height: 1;
  }
  .menu-trigger:hover {
    color: var(--text);
    border-color: var(--text-muted);
  }
  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 0.4rem);
    min-width: 10.5rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.25rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    z-index: 10;
  }
  .menu-item {
    width: 100%;
    background: none;
    border: none;
    color: var(--text);
    text-align: left;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    font-size: 0.85rem;
    cursor: pointer;
  }
  .menu-item:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  .menu-item--danger {
    color: var(--error);
  }
  .blocks-me-banner {
    color: var(--text-muted);
    font-size: 0.85rem;
    font-style: italic;
  }
  .block-status {
    margin-top: 0.5rem;
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .empty {
    color: var(--text-secondary);
  }
  .retry-link {
    background: none;
    border: none;
    color: var(--cyan);
    cursor: pointer;
    font-size: inherit;
    text-decoration: underline;
    padding: 0;
  }

  /* Vote Streak Badge */
  .streak-badge {
    display: inline-block;
    margin-top: 0.5rem;
    padding: 0.25rem 0.75rem;
    background: linear-gradient(135deg, rgba(255, 100, 0, 0.15), rgba(255, 50, 50, 0.15));
    border: 1px solid rgba(255, 100, 0, 0.3);
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #ff8c00;
  }
</style>
